const DISALLOWED_TAGS = [
  "script",
  "style",
  "meta",
  "link",
  "title",
  "xml",
  "iframe",
  "object",
  "embed",
];

const ORDERED_MARKER = /^\s*(?:\d+|[a-zа-яё])(?:[.)])\s+/i;
const UNORDERED_MARKER = /^\s*(?:[•·▪◦‣‒–—-])\s+/;

const normaliseCellText = (value) =>
  (value || "").replace(/\u00a0/g, " ").replace(/\s+\n/g, "\n").trim();

const safeHref = (href) => {
  if (!href) return "";
  const value = href.trim();
  return /^(https?:|mailto:|#|\/)/i.test(value) ? value : "";
};

const removeWordListMarker = (element) => {
  const walker = element.ownerDocument.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );
  let firstText = walker.nextNode();
  while (firstText && !/\S/.test(firstText.nodeValue)) {
    firstText = walker.nextNode();
  }
  if (firstText) {
    firstText.nodeValue = firstText.nodeValue.replace(
      ORDERED_MARKER,
      ""
    ).replace(UNORDERED_MARKER, "");
  }
};

const normaliseWordLists = (root) => {
  Array.from(root.querySelectorAll("p, div")).forEach((element) => {
    const className = element.getAttribute("class") || "";
    const style = element.getAttribute("style") || "";
    const isWordList = /MsoListParagraph/i.test(className) || /mso-list/i.test(style);
    if (!isWordList) return;

    const text = normaliseCellText(element.textContent);
    const ordered = ORDERED_MARKER.test(text);
    const unordered = UNORDERED_MARKER.test(text);
    if (!ordered && !unordered) return;

    element.querySelectorAll('[style*="mso-list"]').forEach((node) => node.remove());
    removeWordListMarker(element);

    const list = root.ownerDocument.createElement(ordered ? "ol" : "ul");
    const item = root.ownerDocument.createElement("li");
    while (element.firstChild) item.appendChild(element.firstChild);
    list.appendChild(item);
    element.replaceWith(list);
  });
};

const keepInlineFormatting = (element) => {
  const style = element.style;
  if (!style) return;
  const wrappers = [];
  if (style.fontWeight === "bold" || parseInt(style.fontWeight, 10) >= 600) {
    wrappers.push("strong");
  }
  if (style.fontStyle === "italic") wrappers.push("em");
  if (/underline/i.test(style.textDecoration || "")) wrappers.push("u");

  wrappers.reverse().forEach((tagName) => {
    const wrapper = element.ownerDocument.createElement(tagName);
    while (element.firstChild) wrapper.appendChild(element.firstChild);
    element.appendChild(wrapper);
  });
};

const extractTable = (table) => {
  const rows = Array.from(table.rows || [])
    .map((row) =>
      Array.from(row.cells || []).map((cell) => ({
        text: normaliseCellText(cell.innerText || cell.textContent),
      }))
    )
    .filter((row) => row.length > 0);
  return rows.length ? rows : null;
};

const replaceTables = (root) => {
  Array.from(root.querySelectorAll("table"))
    .reverse()
    .forEach((table) => {
      const rows = extractTable(table);
      if (!rows) return;
      const marker = root.ownerDocument.createElement("div");
      marker.dataset.wordTable = JSON.stringify(rows);
      // A marker needs text so draft-convert keeps it as a block.
      marker.textContent = "\u200B";
      table.replaceWith(marker);
    });
};

const cleanElements = (root) => {
  Array.from(root.querySelectorAll("*")).forEach((element) => {
    const tag = element.tagName.toLowerCase();
    if (
      DISALLOWED_TAGS.includes(tag) ||
      tag.indexOf(":") !== -1 ||
      tag === "o:p"
    ) {
      element.remove();
      return;
    }

    keepInlineFormatting(element);
    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const keepWordTable = tag === "div" && name === "data-word-table";
      const keepLink = tag === "a" && name === "href" && safeHref(attribute.value);
      if (!keepWordTable && !keepLink) element.removeAttribute(attribute.name);
      if (tag === "a" && name === "href" && !keepLink) element.removeAttribute(attribute.name);
    });
  });
};

/**
 * Keeps only editor-supported document structure from an external clipboard.
 * Word-specific styles, classes, comments and XML are deliberately discarded.
 */
export const sanitizePastedHTML = (html) => {
  if (!html || typeof DOMParser === "undefined") return "";
  const document = new DOMParser().parseFromString(html, "text/html");
  const root = document.body;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT, null, false);
  const comments = [];
  while (walker.nextNode()) comments.push(walker.currentNode);
  comments.forEach((comment) => comment.remove());

  normaliseWordLists(root);
  replaceTables(root);
  cleanElements(root);
  return root.innerHTML;
};
